import React, { useRef, useState } from "react";
import styled from "styled-components";
import TooltipTrigger from "react-popper-tooltip";
import Icon from "../../../../ui/Icon";
import {
  useRenameSectionMutation,
  renameSectionMutationOptions,
  JustSectionFragment,
  useDeleteSectionMutation,
  deleteSectionMutationOptions,
  useMoveSectionToCookbookMutation,
  moveSectionToCookbookMutationOptions,
  useMoveRecipeSectionsMutation,
  moveRecipeSectionsMutationOptions,
  RecipeNameFragment,
  CookbookAccessLevel
} from "@saffron/controllers";
import { validSectionText } from "@saffron/common";
import { LineModalForm } from "../../../../ui/LineModalForm";
import { Modal } from "../../../../ui/Modal";
import { Draggable } from "react-beautiful-dnd";
import { ListButton } from "../../../../ui/ListButton";
import { theme } from "../../../../ui/theme";
import { PickCookbookModal } from "../../../shared/PickCookbookModal";
import { notificationState } from "../../../misc/notifications/NotificationState";
import { useDrop } from "react-dnd";
import { ItemTypes } from "../../../menuPlanner/shared/ItemTypes";

interface Props {
  section: JustSectionFragment;
  onSectionClick: (id: string) => any;
  cookbookId: string;
  selected?: boolean;
  enableDrag?: boolean;
  index: number;
  onDelete?: () => void;
  cookbookAccessLevel: CookbookAccessLevel;
}

const Item = styled("div")<{ selected?: boolean; isOver?: boolean }>`
  line-height: 18.9px;
  cursor: pointer;
  display: flex;
  font-size: 0.875rem;
  ${p =>
    p.selected
      ? `
            background-color: ${theme.selectedColor};
            color: #fff;
          `
      : `
          &:hover {
            background-color: ${theme.hoverColor};
            color: #0d0d0d;
          }
            `};
  ${p =>
    p.isOver
      ? `
  background-color: #F6F3E1;
            color: #0d0d0d;
  `
      : ``}
`;

export const SectionItem: React.FC<Props> = ({
  section,
  selected,
  cookbookId,
  onSectionClick,
  onDelete,
  enableDrag,
  cookbookAccessLevel,
  index
}) => {
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [moveRecipe] = useMoveRecipeSectionsMutation();
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.RECIPE_IN_SECTION,
    drop: ({ recipe, sectionId: currentSectionId }: any) => {
      moveRecipe(
        moveRecipeSectionsMutationOptions(
          {
            options: {
              recipeId: recipe.id,
              targetSectionId: section.id,
              sectionIdToRemove: currentSectionId
            }
          },
          currentSectionId,
          recipe as RecipeNameFragment
        )
      ).then(() => {
        notificationState.send({
          text: "successfully moved",
          variant: "success"
        });
      });
    },
    collect: mon => ({
      isOver: !!mon.isOver()
    })
  });
  const [renameSection] = useRenameSectionMutation();
  const [
    moveSectionToCookbook,
    { loading: moveSectionToCookbookLoading }
  ] = useMoveSectionToCookbookMutation();
  const [
    deleteSection,
    { loading: deleteSectionLoading }
  ] = useDeleteSectionMutation();
  const tooltip = useRef<any>();

  return (
    <>
      <Modal
        isOpen={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        title="Permanently Delete Section"
        mainButtonText="DELETE"
        buttonVariant="red"
        secondaryButtonText="CANCEL"
        secondaryButtonClick={() => setDeleteModalOpen(false)}
        loading={deleteSectionLoading}
        mainButtonClick={async () => {
          await deleteSection(
            deleteSectionMutationOptions({ id: section.id }, cookbookId)
          );
          setDeleteModalOpen(false);
          if (onDelete) {
            onDelete();
          }
        }}
      >
        <div>
          Deleting a section cannot be undone and will permanently delete any of
          the recipes inside of it.
        </div>
      </Modal>
      <LineModalForm
        open={renameModalOpen}
        label="section name"
        title="Rename Section"
        submit={async text => {
          await renameSection(
            renameSectionMutationOptions({
              __typename: "Section",
              id: section.id,
              owner: section.owner,
              position: section.position,
              name: text
            })
          );
        }}
        initialText={section.name}
        closeModal={() => setRenameModalOpen(false)}
        validationSchema={validSectionText}
      />
      <Draggable
        isDragDisabled={
          !enableDrag || cookbookAccessLevel === CookbookAccessLevel.Viewer
        }
        draggableId={section.id}
        index={index!}
      >
        {(dragProvided, { isDragging }) => {
          return (
            <Item
              ref={x => {
                dragProvided.innerRef(x);
                drop(x);
              }}
              {...dragProvided.draggableProps}
              {...dragProvided.dragHandleProps}
              selected={selected || isDragging || isOver}
              isOver={isOver}
              onClick={() => onSectionClick(section.id)}
            >
              <div
                style={{
                  flex: 1,
                  padding: "10px 1.4em",
                  paddingRight: 4
                }}
              >
                {section.name}
              </div>
              {selected &&
              cookbookAccessLevel !== CookbookAccessLevel.Viewer ? (
                <TooltipTrigger
                  ref={tooltip}
                  placement="bottom"
                  trigger="click"
                  tooltip={({ tooltipRef, getTooltipProps }) => {
                    return (
                      <div
                        {...getTooltipProps({
                          ref: tooltipRef,
                          className: "tooltip-container",
                          style: {
                            width: 185,
                            paddingBottom: 5,
                            paddingTop: 5,
                            borderRadius: 4,
                            backgroundColor: "#fff",
                            boxShadow: "0 3px 12px rgba(27,31,35,.15)",
                            border: "1px solid rgba(27,31,35,.15)"
                          }
                        })}
                      >
                        <ListButton
                          onClick={() => {
                            setRenameModalOpen(true);
                            if (tooltip.current) {
                              tooltip.current.hideTooltip();
                            }
                          }}
                        >
                          Rename
                        </ListButton>
                        {cookbookAccessLevel === CookbookAccessLevel.Owner ? (
                          <ListButton
                            onClick={() => {
                              setMoveModalOpen(true);
                              if (tooltip.current) {
                                tooltip.current.hideTooltip();
                              }
                            }}
                          >
                            Move
                          </ListButton>
                        ) : null}
                        <ListButton
                          onClick={() => {
                            setDeleteModalOpen(true);
                            if (tooltip.current) {
                              tooltip.current.hideTooltip();
                            }
                          }}
                        >
                          Delete
                        </ListButton>
                      </div>
                    );
                  }}
                >
                  {({ getTriggerProps, triggerRef }) => (
                    <div>
                      <div
                        {...getTriggerProps({
                          ref: triggerRef,
                          className: "trigger",
                          style: {
                            marginTop: 2.5,
                            padding: 10,
                            cursor: "pointer"
                          }
                        })}
                      >
                        <Icon
                          fill="rgba(255, 255, 255, 0.5)"
                          height="1em"
                          width="1em"
                          name="dots"
                        />
                      </div>
                    </div>
                  )}
                </TooltipTrigger>
              ) : null}
            </Item>
          );
        }}
      </Draggable>
      <PickCookbookModal
        buttonText="MOVE"
        cookbookId={cookbookId}
        onRequestClosed={() => setMoveModalOpen(false)}
        open={moveModalOpen}
        title="Choose Cookbook:"
        onSubmit={async cb => {
          if (!cb) {
            return;
          }

          await moveSectionToCookbook(
            moveSectionToCookbookMutationOptions(
              {
                input: { cookbookId: cb.id, sectionId: section.id }
              },
              section,
              cookbookId
            )
          );
          notificationState.send({
            text: "successfully moved to " + cb.name,
            variant: "success"
          });
        }}
        loading={moveSectionToCookbookLoading}
      />
    </>
  );
};
